/**
 * Browser-compatible translation utility using Google Translate's web interface
 * This uses a CORS proxy approach to access Google Translate from the browser
 */

/**
 * Translates text from Armenian (hy) to target language using Google Translate
 * @param {string} text - Text to translate (Armenian)
 * @param {string} targetLang - Target language code ('ru' for Russian, 'en' for English)
 * @returns {Promise<string>} Translated text
 */
export const translateText = async (text, targetLang = 'en') => {
    if (!text || !text.trim()) {
        return '';
    }

    try {
        // Use Google Translate's web API via a CORS proxy
        // Note: This is a free, unofficial method. For production, consider using
        // Google Cloud Translation API or a backend proxy
        const response = await fetch(
            `https://translate.googleapis.com/translate_a/single?client=gtx&sl=hy&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }
        );

        if (!response.ok) {
            throw new Error(`Translation failed: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Extract translated text from the response
        if (data && data[0] && data[0][0] && data[0][0][0]) {
            return data[0].map(item => item[0]).join('');
        }
        
        throw new Error('Invalid translation response format');
    } catch (error) {
        console.error(`Translation error (${targetLang}):`, error);
        throw error;
    }
};

/**
 * Translates text from Armenian to both Russian and English
 * @param {string} text - Text to translate (Armenian)
 * @returns {Promise<{ru: string, en: string}>} Object with Russian and English translations
 */
export const translateToRuAndEn = async (text) => {
    if (!text || !text.trim()) {
        return { ru: '', en: '' };
    }

    try {
        const [ruTranslation, enTranslation] = await Promise.all([
            translateText(text, 'ru'),
            translateText(text, 'en')
        ]);

        return {
            ru: ruTranslation,
            en: enTranslation
        };
    } catch (error) {
        console.error('Translation error:', error);
        throw error;
    }
};

/**
 * Translates multiple texts from Armenian to Russian and English
 * @param {string[]} texts - Array of Armenian texts to translate
 * @returns {Promise<Array<{ru: string, en: string}>>} Array of translation objects
 */
export const translateMultiple = async (texts) => {
    if (!texts || texts.length === 0) {
        return [];
    }

    try {
        // Translate all texts in parallel with a small delay to avoid rate limiting
        const translationPromises = texts.map((text, index) => 
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(translateToRuAndEn(text));
                }, index * 100); // 100ms delay between requests
            })
        );
        return await Promise.all(translationPromises);
    } catch (error) {
        console.error('Batch translation error:', error);
        throw error;
    }
};
